import React, { useEffect, useState } from 'react';
import { apiV1 } from '@/api/client';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Plus, Trash2, ShieldCheck } from 'lucide-react';

/**
 * The "Custom tracking" editor for a landing page — an ID box, never a script
 * box, mirroring Imprint. The author picks a supported tag type and supplies
 * only the ID; the platform renders the tag. There is deliberately no
 * free-text script field.
 *
 * The type list and the ID hints come from /api/v1/pages/tracking-catalogue,
 * i.e. the same table the backend validator enforces, so this can't offer a
 * type the server will reject.
 *
 * value: array of { type, id, conversion_label? }
 * onChange: (nextArray) => void
 */
export default function TrackingTagsEditor({ value = [], onChange }) {
  const [catalogue, setCatalogue] = useState([]);
  const [type, setType] = useState('');
  const [id, setId] = useState('');
  const [label, setLabel] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    apiV1.get('/pages/tracking-catalogue')
      .then((r) => {
        if (cancelled) return;
        const types = r.data?.data?.types || [];
        setCatalogue(types);
        if (types.length) setType((t) => t || types[0].type);
      })
      .catch(() => { /* editor still works; the server re-validates on save */ });
    return () => { cancelled = true; };
  }, []);

  const tags = Array.isArray(value) ? value : [];
  const selected = catalogue.find((c) => c.type === type);

  const add = () => {
    setError('');
    const cleanId = id.trim();
    if (!cleanId) { setError('Enter the ID.'); return; }
    if (tags.some((t) => t.type === type && t.id === cleanId)) {
      setError('That tag is already added.'); return;
    }
    const tag = { type, id: cleanId };
    if (selected?.takes_label && label.trim()) tag.conversion_label = label.trim();
    onChange([...tags, tag]);
    setId('');
    setLabel('');
  };

  const remove = (i) => onChange(tags.filter((_, idx) => idx !== i));

  const labelFor = (t) => catalogue.find((c) => c.type === t)?.label || t;

  return (
    <div className="space-y-3" data-testid="tracking-tags-editor">
      <div className="flex items-start gap-2 rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        <span>
          Add an analytics or ad-platform ID and we render the tag for you — you never paste a
          script. Tags load only after a visitor accepts the consent banner on the page.
        </span>
      </div>

      {/* Existing tags */}
      {tags.length > 0 && (
        <div className="space-y-1.5" data-testid="tracking-tags-list">
          {tags.map((t, i) => (
            <div key={`${t.type}-${t.id}-${i}`} className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm">
              <span className="font-medium">{labelFor(t.type)}</span>
              <code className="rounded bg-muted px-1.5 py-0.5 text-xs">{t.id}</code>
              {t.conversion_label && (
                <span className="text-xs text-muted-foreground">label: {t.conversion_label}</span>
              )}
              <Button
                type="button" variant="ghost" size="icon"
                className="ml-auto h-7 w-7 text-muted-foreground hover:text-destructive"
                onClick={() => remove(i)}
                data-testid={`tracking-remove-${i}`}
                aria-label="Remove tag"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Add row */}
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-[minmax(0,180px)_1fr_auto]">
        <select
          className="h-9 rounded-md border border-input bg-background px-2 text-sm"
          value={type}
          onChange={(e) => { setType(e.target.value); setError(''); }}
          data-testid="tracking-type"
        >
          {catalogue.map((c) => (
            <option key={c.type} value={c.type}>{c.label}</option>
          ))}
        </select>
        <Input
          value={id}
          onChange={(e) => { setId(e.target.value); setError(''); }}
          placeholder={selected ? selected.hint : 'ID'}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          data-testid="tracking-id"
        />
        <Button type="button" onClick={add} data-testid="tracking-add">
          <Plus className="mr-1 h-4 w-4" /> Add
        </Button>
      </div>

      {selected?.takes_label && (
        <div>
          <Label className="text-xs text-muted-foreground">Conversion label (optional)</Label>
          <Input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. AbC1dEfG"
            className="mt-1"
            data-testid="tracking-label"
          />
        </div>
      )}

      {error && <p className="text-xs text-destructive" data-testid="tracking-error">{error}</p>}
    </div>
  );
}
